import type { MediaKind } from '../types/modal';

export const MAX_FILE_SIZE_MB = 50;

const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export interface AcceptSpec {
  /** MIME types for the file input's `accept` attribute. */
  mimeTypes: string[];
  /** Lowercase extensions without the dot, for files browsers type as empty. */
  extensions: string[];
}

export const ACCEPT_BY_KIND: Record<Extract<MediaKind, 'image' | 'video' | 'audio'>, AcceptSpec> = {
  image: {
    mimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
    extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp'],
  },
  video: {
    mimeTypes: ['video/mp4', 'video/webm', 'video/quicktime'],
    extensions: ['mp4', 'webm', 'mov'],
  },
  audio: {
    mimeTypes: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/ogg'],
    extensions: ['mp3', 'wav', 'ogg'],
  },
};

export type MediaFileError =
  | { reason: 'type'; extensions: string[] }
  | { reason: 'size'; maxMb: number };

export interface MediaFileValidation {
  isValid: boolean;
  error?: MediaFileError;
}

export function acceptAttribute(kind: keyof typeof ACCEPT_BY_KIND): string {
  const spec = ACCEPT_BY_KIND[kind];
  return [...spec.mimeTypes, ...spec.extensions.map((ext) => `.${ext}`)].join(',');
}

export function validateMediaFile(file: File, kind: keyof typeof ACCEPT_BY_KIND): MediaFileValidation {
  const spec = ACCEPT_BY_KIND[kind];
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  if (!spec.mimeTypes.includes(file.type) && !spec.extensions.includes(extension)) {
    return { isValid: false, error: { reason: 'type', extensions: spec.extensions } };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { isValid: false, error: { reason: 'size', maxMb: MAX_FILE_SIZE_MB } };
  }
  return { isValid: true };
}

/** The kind a dropped file belongs to, so a drop anywhere can route to Upload. */
export function kindForFile(file: File): keyof typeof ACCEPT_BY_KIND | null {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  for (const kind of ['image', 'video', 'audio'] as const) {
    const spec = ACCEPT_BY_KIND[kind];
    if (spec.mimeTypes.includes(file.type) || spec.extensions.includes(extension)) return kind;
  }
  return null;
}
